// Main-thread client for the custom SQLite worker (./sqlite-worker.ts).
// Spawns the worker on first call, talks to it via a small request /
// response message protocol with promises queued by message id.
//
// Why a custom worker rather than sqlite-wasm's Worker1Promiser: see
// the comment at the top of sqlite-worker.ts. Tl;dr: the promiser's
// auto-installed `opfs` VFS needs SharedArrayBuffer (cross-origin
// isolation), which we can't enable without breaking our cross-origin
// fetches. The SAH Pool VFS doesn't need it but isn't auto-installed,
// so we install it ourselves in our own worker.

// Vite's `?worker` suffix produces a constructor for a bundled module
// worker. The worker bundle includes the sqlite-wasm shim; the actual
// wasm file is loaded by sqlite-wasm at runtime via
// `new URL("sqlite3.wasm", import.meta.url)`, which resolves to
// /assets/sqlite3.wasm — placed there by our build script.
import SqliteWorker from "./sqlite-worker?worker";

interface Pending {
  resolve: (rows: unknown[] | undefined) => void;
  reject: (err: Error) => void;
}

let worker: Worker | null = null;
let readyPromise: Promise<void> | null = null;
const pending = new Map<string, Pending>();
let nextId = 0;

function ensureWorker(): Promise<void> {
  if (readyPromise) return readyPromise;
  readyPromise = new Promise<void>((resolve, reject) => {
    const w = new SqliteWorker();
    worker = w;
    w.addEventListener("message", (ev: MessageEvent) => {
      const msg = ev.data;
      if (msg.type === "ready") { resolve(); return; }
      if (msg.type === "init-error") {
        reject(new Error(msg.error ?? "SQLite worker init failed"));
        return;
      }
      // Otherwise it's a response to a request — look up by id.
      const p = pending.get(msg.id);
      if (!p) return; // stray message; ignore
      pending.delete(msg.id);
      if (msg.ok) p.resolve(msg.rows);
      else p.reject(new Error(msg.error ?? "SQLite worker error"));
    });
    w.addEventListener("error", (ev: ErrorEvent) => {
      reject(new Error(`SQLite worker errored: ${ev.message ?? "(no message)"}`));
    });
  });
  return readyPromise;
}

async function send<T = unknown[]>(
  op: "exec" | "query",
  sql: string,
  bind?: unknown[],
): Promise<T | undefined> {
  await ensureWorker();
  return new Promise<T | undefined>((resolve, reject) => {
    const id = `m${nextId++}`;
    pending.set(id, {
      resolve: (rows) => resolve(rows as T | undefined),
      reject,
    });
    worker!.postMessage({ id, op, sql, bind });
  });
}

/** Run a one-shot statement that returns no rows (CREATE, INSERT,
 *  UPDATE, DELETE). Parameter placeholders use the `?` style. */
export async function exec(sql: string, bind?: unknown[]): Promise<void> {
  await send("exec", sql, bind);
}

/** Run a SELECT and return rows as plain objects (column-name keyed). */
export async function query<T = Record<string, unknown>>(
  sql: string,
  bind?: unknown[],
): Promise<T[]> {
  const rows = await send<unknown[]>("query", sql, bind);
  return (rows ?? []) as T[];
}

/** Run multiple statements in one BEGIN/COMMIT transaction.
 *  Rolls back on throw. The callback receives an `exec` function
 *  that runs each statement on the same worker connection. */
export async function transaction(
  fn: (exec: (sql: string, bind?: unknown[]) => Promise<void>) => Promise<void>,
): Promise<void> {
  await exec("BEGIN");
  try {
    await fn(exec);
    await exec("COMMIT");
  } catch (e) {
    try { await exec("ROLLBACK"); } catch { /* swallow */ }
    throw e;
  }
}
